





































































































































































































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import store from "@/store";
import moment from "moment";
//  @types
import { LoggingHistoryConfig } from "@/types/loggingHistoryConfig";
import { ExtendedVessel } from "@/types/vessel";
import { DataHistoryFilter, GranularityItem } from "@/types/loggingHistoryConfig";
//  @components
import FeatureLogVariables from "./FeatureLogVariables.vue";
import DateRangeSelect from "./DateRangeSelect.vue";
import VoyageSelect from "./VoyageSelect.vue";
//  @modules
import { getModule } from "vuex-module-decorators";
import VesselsModule from "@/store/clients/Vessels.module";
import DataHistoryModule from "@/store/clients/DataHistory.module";
import SnackbarModule from "@/store/clients/Snackbar.module";
import LoggingHistoryClient from "@/Scripts/clients/logging-history-client";

const Vessels = getModule(VesselsModule, store);
const DataHistory = getModule(DataHistoryModule, store);
const Snackbar = getModule(SnackbarModule, store);

type DateRangeType = "AllValues" | "LastVoyage" | "LastPeriod" | "SetPeriod";
type ChangeableFilterKeys = "logVariableIds" | "cdsFeatureIds" | "selectedLogVariableIds" | "selectedCdsFeatureIds";

@Component({
  components: {
    FeatureLogVariables,
    DateRangeSelect,
    VoyageSelect,
  },
})
export default class DataHistoryFiltersPanel extends Vue {
  // @Data
  panel = null;
  exportExcelLoading = false;
  exportCSVLoading = false;
  isSubmitted = false;
  lastDaysCount: number | string = 7;
  DataHistoryConfig: LoggingHistoryConfig = {} as LoggingHistoryConfig;
  Filter: DataHistoryFilter = {
    dateRange: null as null | [string, string],
    dateRangeType: "AllValues" as DateRangeType,
    granularity: null as GranularityItem | null,
    logVariableIds: "" as string,
    cdsFeatureIds: "" as string,
    selectedCdsFeatureIds: "" as string,
    selectedLogVariableIds: "" as string,
    filterOutDataOutliers: false,
    filterOutMinMax: false,
    lastVoyage: null,
  };
  intervalLimit: { [key: string]: number } = {
    Raw: this.daysToMinutes(1),
    Minute: this.daysToMinutes(2),
    QuarterHour: this.daysToMinutes(14),
    Hour: this.daysToMinutes(90),
  };

  // @Watchers
  @Watch("Filter.dateRange", { immediate: true, deep: true })
  onDateRangeChange(newVal: [string, string] | null) {
    if (!this.Filter.granularity || !this.isDataIntervalAllowed(this.Filter.granularity.value)) {
      // Find the first allowed granularity
      const allowed = this.granularityList.find(g => this.isDataIntervalAllowed(g.value));
      if (allowed) {
        this.Filter.granularity = allowed;
      }
    }
    // If a manual date range is being used (not LastVoyage or LastPeriod) ensure voyage selection is cleared
    if (this.Filter.dateRangeType !== "LastVoyage" && this.Filter.dateRangeType !== "LastPeriod" && this.Filter.lastVoyage) {
      this.Filter.lastVoyage = null;
    }
  }

  @Watch("Filter.dateRangeType")
  onDateRangeTypeChanged(newVal: DateRangeType) {
    if (newVal !== "LastVoyage" && this.Filter.lastVoyage) {
      this.Filter.lastVoyage = null;
    }
    // When switching to LastPeriod, calculate range if lastDaysCount has value
    if (newVal === "LastPeriod" && this.lastDaysCount) {
      this.calculateLastDaysRange();
    } else if (newVal === "LastVoyage" || newVal === "AllValues") {
      // Clear date range when switching to LastVoyage or AllValues
      this.Filter.dateRange = null;
    }
  }

  // @Getters
  get vessel(): ExtendedVessel | null {
    if (!Vessels.currentVessel) return null;
    return Vessels.currentVessel;
  }

  get vesselLogDataMinDate(): string {
    if (!this.vessel) return moment.utc().subtract(5, "year").toISOString();
    return this.vessel.logDataMinDate;
  }

  get vesselLogDataMaxDate(): string {
    if (!this.vessel) return moment.utc().toISOString();
    return this.vessel.logDataMaxDate;
  }

  get fromDateTime(): string {
    if (this.Filter.dateRangeType === "LastVoyage" && this.Filter.lastVoyage?.startDate) {
      return moment.utc(this.Filter.lastVoyage.startDate).toISOString();
    }
    if (this.Filter.dateRange && this.Filter.dateRange[0]) {
      return moment.utc(this.Filter.dateRange[0], "YYYY-MM-DDTHH:mm[Z]").toISOString();
    }
    if (this.Filter.dateRangeType === "AllValues") return this.vesselLogDataMinDate;
    return "";
  }

  get toDateTime(): string {
    if (this.Filter.dateRangeType === "LastVoyage" && this.Filter.lastVoyage?.endDate) {
      return moment.utc(this.Filter.lastVoyage.endDate).toISOString();
    }
    if (this.Filter.dateRange && this.Filter.dateRange[1]) {
      const to = moment.utc(this.Filter.dateRange[1], "YYYY-MM-DDTHH:mm[Z]");
      if (this.Filter.dateRange[0] === this.Filter.dateRange[1]) return to.endOf("day").toISOString();
      return to.toISOString();
    }
    if (this.Filter.dateRangeType === "AllValues") return this.vesselLogDataMaxDate;
    return "";
  }

  get granularityList(): { text: string; hint: string; value: string; disabledHint?: string }[] {
    const all = [
      { text: "Auto", hint: "Lowest available granularity for selected range", value: "Auto" },
      { text: "Daily", hint: "One point per day (average)", value: "Day" },
      { text: "Hourly", hint: "One point per hour (average)", disabledHint: "Available for date ranges of 6 months or less", value: "Hour" },
      {
        text: "Quarter-Hourly",
        hint: "One point per 15 minutes (average)",
        disabledHint: "Available for date ranges of 15 days or less",
        value: "QuarterHour",
      },
      { text: "Minutely", hint: "One point per minute (average)", disabledHint: "Available for date ranges of 3 days or less", value: "Minute" },
      { text: "Raw", hint: "All recorded values", disabledHint: "Available for date ranges of 1 day or less", value: "Raw" },
    ];
    if (this.isOnlyCdsFeatures) {
      return all.map(item => ({
        ...item,
        disabledHint: this.cdsFeatureDisabledHint(item.value),
        hint: item.value === "Auto" ? "Lowest available granularity for features" : item.hint,
      }));
    }
    return all;
  }

  get hasLogVariables(): boolean {
    return !!this.Filter.selectedLogVariableIds && this.Filter.selectedLogVariableIds.length > 0;
  }

  get hasCdsFeatures(): boolean {
    return !!this.Filter.selectedCdsFeatureIds && this.Filter.selectedCdsFeatureIds.length > 0;
  }

  get isOnlyCdsFeatures(): boolean {
    return this.hasCdsFeatures && !this.hasLogVariables;
  }

  get hasLogVariablesOrFeatures(): boolean {
    return this.hasLogVariables || this.hasCdsFeatures;
  }

  get isSubmitDisabled(): boolean {
    return !this.hasLogVariablesOrFeatures || !this.Filter.granularity;
  }

  get cdsFeatureDisabledHint(): (value: string) => string | undefined {
    return (value: string) => {
      if (value !== "Hour") return "Not available for features";
      return undefined;
    };
  }

  get isHourGranularityWarning(): (value: string) => boolean {
    return (value: string) => !this.isOnlyCdsFeatures && value === "Hour" && this.isAnyFeatureSelected;
  }

  get isAnyFeatureSelected(): boolean {
    return typeof this.Filter.selectedCdsFeatureIds === "string" && this.Filter.selectedCdsFeatureIds.length > 0;
  }

  // @Methods
  onFilterChange(value: string | string[], filterKey: ChangeableFilterKeys) {
    this.Filter[filterKey] = Array.isArray(value) ? value.join(",") : value;
  }

  async submit(): Promise<void> {
    try {
      this.DataHistoryConfig = {
        ...this.DataHistoryConfig, // Preserve existing config properties
        cdsFeatureIds: this.Filter.cdsFeatureIds ?? "",
        logVariableIds: this.Filter.logVariableIds ?? "",
        timeConfigurationType: this.Filter.dateRangeType,
        lastNumberDays: Number(this.lastDaysCount) || 7,
        lastVoyage: this.Filter.lastVoyage ?? undefined,
        fromDateTime: this.fromDateTime,
        toDateTime: this.toDateTime,
        dataInterval: (this.Filter.granularity?.value as "Auto" | "Day" | "Hour" | "Minute" | "Raw" | "Quarter") ?? "Auto",
        filterOutliers: this.Filter.filterOutDataOutliers,
        filterMinMax: this.Filter.filterOutMinMax,
      };
      await DataHistory.updateConfig(this.DataHistoryConfig);
      this.$emit("submit", { config: this.DataHistoryConfig, filter: this.Filter });
      this.isSubmitted = true;
      Snackbar.showSnackbar({
        text: "Data history configuration updated successfully.",
        color: "success",
        timeout: 3000,
      });
    } catch (error) {
      Snackbar.showSnackbar({
        text: "Error updating data history configuration.",
        color: "error",
      });
      console.error("Error during submit:", error);
    }
  }

  daysToMinutes(days: number): number {
    return days * 24 * 60;
  }

  getDateRangeFromConfig(config: LoggingHistoryConfig): [string, string] | null {
    if (config.timeConfigurationType === "AllValues") {
      return null;
    }
    const from = config.fromDateTime;
    const to = config.toDateTime;
    if (from && to) {
      return [moment.utc(from).format("YYYY-MM-DDTHH:mm"), moment.utc(to).format("YYYY-MM-DDTHH:mm")];
    }
    return null;
  }

  async initializeFilter(): Promise<void> {
    if (!this.vessel?.id) return;

    this.DataHistoryConfig = (await DataHistory.fetchConfigByVesselId(this.vessel.id)) ?? ({} as LoggingHistoryConfig);
    if (this.DataHistoryConfig.lastNumberDays) {
      this.lastDaysCount = this.DataHistoryConfig.lastNumberDays;
    }
    this.Filter = {
      dateRange: this.getDateRangeFromConfig(this.DataHistoryConfig),
      dateRangeType: this.DataHistoryConfig.timeConfigurationType ?? "AllValues",
      granularity: this.granularityList.find(g => g.value === this.DataHistoryConfig.dataInterval) ?? this.granularityList[0],
      logVariableIds: this.DataHistoryConfig.logVariableIds ?? "",
      cdsFeatureIds: this.DataHistoryConfig.cdsFeatureIds ?? "",
      selectedCdsFeatureIds: "",
      selectedLogVariableIds: "",
      filterOutDataOutliers: this.DataHistoryConfig.filterOutliers ?? false,
      filterOutMinMax: this.DataHistoryConfig.filterMinMax ?? false,
      lastVoyage: this.DataHistoryConfig.timeConfigurationType === "LastVoyage" ? this.DataHistoryConfig.lastVoyage ?? null : null,
    };
  }

  exportExcel(): void {
    if (!this.vessel) return;
    this.exportExcelLoading = true;
    LoggingHistoryClient.DownloadExcel(this.vessel.id);
    setTimeout(() => {
      this.exportExcelLoading = false;
    }, 2000);
  }

  exportCSV(): void {
    if (!this.vessel) return;
    this.exportCSVLoading = true;
    LoggingHistoryClient.DownloadCsv(this.vessel.id);
    setTimeout(() => {
      this.exportCSVLoading = false;
    }, 2000);
  }

  isDataIntervalAllowed(value: string): boolean {
    if (value === "Auto") return true;
    if (this.isOnlyCdsFeatures) {
      return value === "Hour";
    }
    if (!this.Filter.dateRange || !this.Filter.dateRange[0] || !this.Filter.dateRange[1]) return true;

    const start = moment.utc(this.Filter.dateRange[0], "YYYY-MM-DDTHH:mm[Z]");
    const end = moment.utc(this.Filter.dateRange[1], "YYYY-MM-DDTHH:mm[Z]");
    const diffDays = end.diff(start, "days") + 1;

    switch (value) {
      case "Raw":
        return diffDays <= 1;
      case "Minute":
        return diffDays <= 3;
      case "QuarterHour":
        return diffDays <= 15;
      case "Hour":
        return diffDays <= 183; // 6 months ≈ 183 days
      case "Day":
      case "Auto":
      default:
        return true;
    }
  }

  onVoyageSelected(voyage: any) {
    if (voyage) {
      // set voyage selection and reset manual date range UI (DateRangeSelect returns to default)
      this.Filter.lastVoyage = voyage;
      this.Filter.dateRangeType = "LastVoyage";
      this.Filter.dateRange = null; // clear manual range so DateRangeSelect shows default
    } else {
      if (this.Filter.dateRangeType === "LastVoyage") {
        // Reset to AllValues when voyage cleared
        this.Filter.dateRangeType = "AllValues";
        this.Filter.dateRange = null;
        this.Filter.lastVoyage = null;
      }
    }
  }

  onLastDaysCountChange() {
    if (this.Filter.dateRangeType === "LastPeriod") {
      this.calculateLastDaysRange();
    }
  }

  calculateLastDaysRange() {
    const days = typeof this.lastDaysCount === "number" ? this.lastDaysCount : parseInt(String(this.lastDaysCount), 10);
    if (days && days > 0) {
      const endDate = moment.utc();
      const startDate = moment.utc().subtract(days, "days");
      this.Filter.dateRange = [startDate.format("YYYY-MM-DDTHH:mm"), endDate.format("YYYY-MM-DDTHH:mm")];
    } else {
      this.Filter.dateRange = null;
    }
  }

  // @Hooks
  async created(): Promise<void> {
    // Initialize default values
    await this.initializeFilter();
  }
}
